import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ContextualStanding } from '@/services/standingsService';
import { StandingsTableSkeleton } from '@/components/ScheduleSkeletons';
import TeamInfoDrawer from './TeamInfoDrawer';
import { LeagueLogo } from './LeagueLogo';
import { UnverifiedDelta } from '@/utils/standingsUtils';

interface League {
  playoffsEnabled?: boolean;
  playoffSpots?: number;
  standingsSortBy?: string;
  standingsSource?: string;
}

interface ContextualStandingsTableProps {
  standings: ContextualStanding[];
  loading?: boolean;
  error?: string;
  league?: League;
  leagueId?: string;
  unverifiedDeltas?: Record<string, UnverifiedDelta>;
}

const ContextualStandingsTable: React.FC<ContextualStandingsTableProps> = ({
  standings,
  loading = false,
  error,
  league,
  leagueId,
  unverifiedDeltas
}) => {
  const [currentDivisionIndex, setCurrentDivisionIndex] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState<ContextualStanding | null>(null);
  const [tappedRowKey, setTappedRowKey] = useState<string | null>(null);
  const tapTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const touchCancelledRef = useRef(false);
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  const handleRowTouchStart = useCallback((key: string, e: React.TouchEvent) => {
    if (!leagueId) return;
    touchCancelledRef.current = false;
    const touch = e.touches[0];
    pointerDownPos.current = { x: touch.clientX, y: touch.clientY };
    setTappedRowKey(key);
  }, [leagueId]);

  const handleRowTouchMove = useCallback(() => {
    touchCancelledRef.current = true;
    setTappedRowKey((prev) => prev !== null ? null : prev);
  }, []);

  const handleRowMouseDown = useCallback((e: React.MouseEvent) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleRowClick = useCallback((key: string, team: ContextualStanding, e: React.MouseEvent) => {
    if (!leagueId) return;

    // If the user dragged more than 5px, they were selecting text — don't open drawer
    if (pointerDownPos.current) {
      const dx = Math.abs(e.clientX - pointerDownPos.current.x);
      const dy = Math.abs(e.clientY - pointerDownPos.current.y);
      if (dx > 5 || dy > 5) {
        pointerDownPos.current = null;
        return;
      }
    }
    pointerDownPos.current = null;

    // If text is selected, the user was selecting — don't open drawer
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      return;
    }

    setTappedRowKey(key);
    clearTimeout(tapTimerRef.current);
    tapTimerRef.current = setTimeout(() => setTappedRowKey(null), 400);
    // Clear any text selection before opening drawer to prevent Chrome selection popup
    window.getSelection()?.removeAllRanges();
    setSelectedTeam(team);
  }, [leagueId]);

  const handleDrawerClose = useCallback(() => {
    setSelectedTeam(null);
    setTappedRowKey(null);
  }, []);

  if (loading) {
    return <StandingsTableSkeleton />;
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        Standings unavailable. Add match results to see team positions.
      </Alert>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        No matches played yet
      </Typography>
    );
  }

  // Deduplicate standings by teamId/teamName to prevent duplicate key warnings
  const seen = new Set<string>();
  const deduplicatedStandings = standings.filter(team => {
    const key = team.teamId || team.teamName;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Group standings by division and reorder to put user's team division first
  const divisions = [...new Set(deduplicatedStandings.map(s => s.division).filter(Boolean))];
  const hasDivisions = divisions.length > 0;

  // Find which division has the user's team and put it first
  const userTeamDivision = divisions.find(division =>
    deduplicatedStandings.some(s => s.division === division && s.isUserTeam)
  );

  const reorderedDivisions = userTeamDivision
    ? [userTeamDivision, ...divisions.filter(d => d !== userTeamDivision)]
    : divisions;

  const groupedStandings = hasDivisions
    ? reorderedDivisions.reduce((acc, division) => {
        const divisionName = division || 'Standings';
        acc[divisionName] = deduplicatedStandings.filter(s => s.division === division);
        return acc;
      }, {} as Record<string, ContextualStanding[]>)
    : { 'Standings': deduplicatedStandings };

  const divisionEntries = Object.entries(groupedStandings);
  const hasMultipleDivisions = divisionEntries.length > 1;

  const handlePrevDivision = () => {
    setCurrentDivisionIndex((prev) => 
      prev === 0 ? divisionEntries.length - 1 : prev - 1
    );
  };

  const handleNextDivision = () => {
    setCurrentDivisionIndex((prev) => 
      prev === divisionEntries.length - 1 ? 0 : prev + 1
    );
  };

  const DeltaSpan: React.FC<{ value: number }> = ({ value }) =>
    value > 0 ? (
      <Box component="span" sx={{ color: 'warning.main' }}>({value})</Box>
    ) : null;

  const renderStandingsCards = (divisionStandings: ContextualStanding[]) => {
    const usePointsPerGame = league?.standingsSortBy === 'POINTS_PER_GAME';

    return (
      <Box>
        <Divider />
        {divisionStandings.map((team) => {
          const delta = unverifiedDeltas?.[team.teamName];
          const isInPlayoffs = league?.playoffsEnabled &&
                               league?.playoffSpots &&
                               team.position <= league.playoffSpots;

          const rowKey = `${leagueId || 'league'}-${team.teamId || team.teamName}`;
          const isTapped = tappedRowKey === rowKey;

          const pointsDisplay = usePointsPerGame
            ? (team.pointsPerGame !== undefined && team.pointsPerGame !== null
                ? team.pointsPerGame.toFixed(2)
                : (team.played > 0 ? (team.points / team.played).toFixed(2) : '0.00'))
            : team.points;

          return (
            <React.Fragment key={rowKey}>
              <Box
                onTouchStart={(e) => handleRowTouchStart(rowKey, e)}
                onTouchMove={handleRowTouchMove}
                onMouseDown={handleRowMouseDown}
                onClick={(e) => handleRowClick(rowKey, team, e)}
                sx={{
                  pl: '1px',
                  pr: 1.5,
                  py: 1,
                  cursor: leagueId ? 'pointer' : 'default',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background-color 0.3s ease-out',
                  backgroundColor: isTapped ? 'rgba(0, 0, 0, 0.06)' : (team.isUserTeam ? 'rgba(76, 175, 80, 0.08)' : 'transparent'),
                  borderLeft: isInPlayoffs ? '2px solid #1976d2' : '2px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '@media (hover: hover)': {
                    '&:hover': leagueId ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    } : {},
                  },
                }}
              >
                {/* Position */}
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: team.isUserTeam ? 700 : 500,
                    color: 'text.secondary',
                    minWidth: '16px',
                    textAlign: 'center',
                  }}
                >
                  {team.position}
                </Typography>

                {/* Team Logo */}
                <LeagueLogo
                  leagueName={team.teamName}
                  logoPath={team.logoPath}
                  logoUrl={team.logoUrl}
                  size={24}
                />

                {/* Team name + stats */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: team.isUserTeam ? 700 : 500,
                      color: team.isUserTeam ? 'text.primary' : 'text.secondary',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {team.teamName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: 'text.secondary',
                      fontWeight: team.isUserTeam ? 700 : 400,
                      mt: 0.25,
                    }}
                  >
                    {team.qualityOfPlay !== undefined ? (
                      <>
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                          {team.played}<DeltaSpan value={delta?.played ?? 0} /> played · {team.won}<DeltaSpan value={delta?.won ?? 0} />W {team.drawn}<DeltaSpan value={delta?.drawn ?? 0} />D {team.lost}<DeltaSpan value={delta?.lost ?? 0} />L · {team.attScore ?? 0} att · {team.defScore ?? 0} def · {team.qualityOfPlay} QoP
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          {team.played}<DeltaSpan value={delta?.played ?? 0} /> played · {team.attScore ?? 0} att · {team.defScore ?? 0} def · {team.qualityOfPlay} QoP
                        </Box>
                      </>
                    ) : (
                      <>
                        {team.played}<DeltaSpan value={delta?.played ?? 0} /> played · {team.won}<DeltaSpan value={delta?.won ?? 0} />W {team.drawn}<DeltaSpan value={delta?.drawn ?? 0} />D {team.lost}<DeltaSpan value={delta?.lost ?? 0} />L · {pointsDisplay} {usePointsPerGame ? 'ppg' : 'pts'}
                        {!usePointsPerGame && team.played > 0 && ` · ${(team.pointsPerGame !== undefined && team.pointsPerGame !== null ? team.pointsPerGame : team.points / team.played).toFixed(1)} ppg`}
                      </>
                    )}
                  </Typography>
                </Box>


              </Box>
              <Divider />
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  // Always use carousel layout for consistency
  const currentDivision = divisionEntries[currentDivisionIndex];
  
  return (
    <Box sx={{ mt: hasMultipleDivisions ? -0.5 : 0, userSelect: selectedTeam ? 'none' : 'auto', WebkitUserSelect: selectedTeam ? 'none' : 'auto' }}>
      {/* Division Header with Navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: hasMultipleDivisions ? 0.0625 : 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
          {currentDivision[0]}
        </Typography>
        
        <Box sx={{ flex: 1 }} />
        
        {/* Navigation Buttons */}
        {hasMultipleDivisions && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={handlePrevDivision} 
              size="small"
            >
              <ChevronLeft />
            </IconButton>
            
            <Typography variant="caption" sx={{ mx: 1, color: 'text.secondary' }}>
              {currentDivisionIndex + 1} of {divisionEntries.length}
            </Typography>
            
            <IconButton 
              onClick={handleNextDivision} 
              size="small"
            >
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Current Division Standings */}
      {renderStandingsCards(currentDivision[1])}

      {/* Team Info Bottom Sheet */}
      {leagueId && (
        <TeamInfoDrawer
          open={selectedTeam !== null}
          onClose={handleDrawerClose}
          team={selectedTeam}
          leagueId={leagueId}
          unverifiedDelta={selectedTeam ? unverifiedDeltas?.[selectedTeam.teamName] : undefined}
        />
      )}
    </Box>
  );
};

export default ContextualStandingsTable;
